import React from 'react';

const StepCurrentSituation = ({ formData, updateFormData, nextStep, prevStep, disabled }) => {
  const data = formData.currentSituation;

  const handleChange = (field, value) => {
    updateFormData('currentSituation', { [field]: value });
  };

  const addItem = (type) => {
    const items = [...(data[type] || []), { description: '', value: '' }];
    handleChange(type, items);
  };

  const updateItem = (type, index, field, value) => {
    const items = [...(data[type] || [])];
    items[index] = { ...items[index], [field]: value };
    handleChange(type, items);
  };

  const removeItem = (type, index) => {
    const items = (data[type] || []).filter((_, i) => i !== index);
    handleChange(type, items);
  };

  const renderItemRows = (type, label) => {
    const items = data[type] || [];
    return (
      <>
        <div className="soa-section-banner">
          <i className={type === 'assets' ? 'bi bi-building' : 'bi bi-credit-card'}></i>
          {label}
        </div>

        {items.map((item, idx) => (
          <div key={idx} className="soa-item-row">
            <input
              type="text"
              className="form-control flex-grow-1"
              value={item.description}
              onChange={(e) => updateItem(type, idx, 'description', e.target.value)}
              placeholder="Description"
            />
            <input
              type="number"
              className="form-control"
              style={{ maxWidth: '160px' }}
              value={item.value}
              onChange={(e) => updateItem(type, idx, 'value', e.target.value)}
              placeholder="Value ($)"
            />
            <button className="btn btn-outline-danger btn-sm" onClick={() => removeItem(type, idx)} disabled={disabled}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        ))}

        {items.length > 0 && (
          <div className="text-end mb-2 small fw-bold" style={{ color: 'var(--primary-color)' }}>
            Total: ${items.reduce((sum, i) => sum + (parseFloat(i.value) || 0), 0).toLocaleString('en-AU', { minimumFractionDigits: 0 })}
          </div>
        )}

        <button className="btn btn-outline-primary btn-sm mb-3" onClick={() => addItem(type)} disabled={disabled}>
          <i className="bi bi-plus-lg me-1"></i>Add {type === 'assets' ? 'Asset' : 'Liability'}
        </button>
      </>
    );
  };

  return (
    <fieldset disabled={disabled}>
      <h4 className="soa-step-title">Current Situation</h4>
      <p className="soa-step-subtitle">Document the client's existing financial position</p>

      {renderItemRows('assets', 'Assets')}
      {renderItemRows('liabilities', 'Liabilities')}

      <div className="soa-section-banner">
        <i className="bi bi-piggy-bank"></i>
        Superannuation & Insurance
      </div>

      <div className="row">
        <div className="col-md-6 soa-form-group">
          <label>Superannuation Details</label>
          <textarea
            className="form-control"
            value={data.superannuation}
            onChange={(e) => handleChange('superannuation', e.target.value)}
            placeholder="e.g. AustralianSuper — $450,000 (Balanced option)"
            rows={3}
          />
        </div>
        <div className="col-md-6 soa-form-group">
          <label>Insurance Details</label>
          <textarea
            className="form-control"
            value={data.insurance}
            onChange={(e) => handleChange('insurance', e.target.value)}
            placeholder="e.g. Life insurance $500k through super, Income protection via employer..."
            rows={3}
          />
        </div>
      </div>

      <div className="soa-form-group">
        <label>Current Investment Portfolio</label>
        <textarea
          className="form-control"
          value={data.currentPortfolio}
          onChange={(e) => handleChange('currentPortfolio', e.target.value)}
          placeholder="Describe existing investment holdings, platforms, managed funds, direct shares etc."
          rows={3}
        />
      </div>

      <div className="soa-form-group">
        <label>Additional Notes</label>
        <textarea
          className="form-control"
          value={data.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          placeholder="Any other relevant information about the client's current financial situation"
          rows={2}
        />
      </div>

      {/* Income Items */}
      <div className="soa-section-banner">
        <i className="bi bi-cash-coin"></i>
        Income
      </div>

      {(data.incomeItems || []).map((item, idx) => (
        <div key={idx} className="soa-item-row">
          <input
            type="text"
            className="form-control flex-grow-1"
            value={item.source}
            onChange={(e) => {
              const items = [...(data.incomeItems || [])];
              items[idx] = { ...items[idx], source: e.target.value };
              handleChange('incomeItems', items);
            }}
            placeholder="Source (e.g. Salary, Rental)"
          />
          <input
            type="number"
            className="form-control"
            style={{ maxWidth: '140px' }}
            value={item.amount}
            onChange={(e) => {
              const items = [...(data.incomeItems || [])];
              items[idx] = { ...items[idx], amount: e.target.value };
              handleChange('incomeItems', items);
            }}
            placeholder="Amount ($)"
          />
          <select
            className="form-select"
            style={{ maxWidth: '120px' }}
            value={item.frequency || 'annual'}
            onChange={(e) => {
              const items = [...(data.incomeItems || [])];
              items[idx] = { ...items[idx], frequency: e.target.value };
              handleChange('incomeItems', items);
            }}
          >
            <option value="annual">Annual</option>
            <option value="monthly">Monthly</option>
            <option value="fortnightly">Fortnightly</option>
            <option value="weekly">Weekly</option>
          </select>
          <button className="btn btn-outline-danger btn-sm" onClick={() => {
            handleChange('incomeItems', (data.incomeItems || []).filter((_, i) => i !== idx));
          }} disabled={disabled}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      ))}

      <button className="btn btn-outline-primary btn-sm mb-3" onClick={() => {
        handleChange('incomeItems', [...(data.incomeItems || []), { source: '', amount: '', frequency: 'annual' }]);
      }} disabled={disabled}>
        <i className="bi bi-plus-lg me-1"></i>Add Income Source
      </button>

      {/* Expenditure Items */}
      <div className="soa-section-banner">
        <i className="bi bi-cart-dash"></i>
        Expenditure
      </div>

      {(data.expenditureItems || []).map((item, idx) => (
        <div key={idx} className="soa-item-row">
          <input
            type="text"
            className="form-control flex-grow-1"
            value={item.category}
            onChange={(e) => {
              const items = [...(data.expenditureItems || [])];
              items[idx] = { ...items[idx], category: e.target.value };
              handleChange('expenditureItems', items);
            }}
            placeholder="Category (e.g. Housing, Food, Transport)"
          />
          <input
            type="number"
            className="form-control"
            style={{ maxWidth: '140px' }}
            value={item.amount}
            onChange={(e) => {
              const items = [...(data.expenditureItems || [])];
              items[idx] = { ...items[idx], amount: e.target.value };
              handleChange('expenditureItems', items);
            }}
            placeholder="Amount ($)"
          />
          <select
            className="form-select"
            style={{ maxWidth: '120px' }}
            value={item.frequency || 'annual'}
            onChange={(e) => {
              const items = [...(data.expenditureItems || [])];
              items[idx] = { ...items[idx], frequency: e.target.value };
              handleChange('expenditureItems', items);
            }}
          >
            <option value="annual">Annual</option>
            <option value="monthly">Monthly</option>
            <option value="fortnightly">Fortnightly</option>
            <option value="weekly">Weekly</option>
          </select>
          <button className="btn btn-outline-danger btn-sm" onClick={() => {
            handleChange('expenditureItems', (data.expenditureItems || []).filter((_, i) => i !== idx));
          }} disabled={disabled}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      ))}

      <button className="btn btn-outline-primary btn-sm mb-3" onClick={() => {
        handleChange('expenditureItems', [...(data.expenditureItems || []), { category: '', amount: '', frequency: 'annual' }]);
      }} disabled={disabled}>
        <i className="bi bi-plus-lg me-1"></i>Add Expenditure Item
      </button>

      <div className="soa-step-nav">
        <button className="btn btn-outline-secondary" onClick={prevStep}>
          <i className="bi bi-arrow-left me-1"></i>Back
        </button>
        <button className="btn btn-primary" onClick={nextStep}>
          Next: Objectives & Goals <i className="bi bi-arrow-right ms-1"></i>
        </button>
      </div>
    </fieldset>
  );
};

export default StepCurrentSituation;
